// ── Indicadores tipo devtools dentro del iframe de la vista previa ────────────
//
// Adaptado de abi/screenshot-to-code (MIT), `frontend/src/components/select-and-edit/overlays.ts`.
// Cambios respecto del original: paleta del design system de Evo Hub, y el
// overlay muestra el data-evo-id además del tag, porque acá la identidad del
// elemento es el dato que importa.
//
// Regla central: los overlays NUNCA mutan la página. Se cuelgan de
// documentElement (fuera de <body>) con pointer-events:none, así el outerHTML
// que se captura del elemento seleccionado sale limpio — sin clases de
// selección, sin wrappers, sin atributos nuestros. Eso importa el doble acá,
// porque ese HTML termina guardado como la versión siguiente de la página.
//
// ── Excepción documentada a REGLA #2 (tokens) ────────────────────────────────
// Estos colores son literales, no var(--evo-*), porque el overlay se inyecta en
// el documento del iframe, que carga el CSS del TEMPLATE del cliente y no
// tokens.css. Una var() ahí no resuelve a nada. Los valores están copiados de
// packages/tokens/tokens.css y anotados con el token del que salen; si el token
// cambia, hay que actualizarlos acá a mano.

/** --evo-cyan */
const CYAN = '#00FAC8';
/** --evo-cyan-12 */
const CYAN_12 = 'rgba(0, 250, 200, 0.12)';
/** --evo-cyan-18 */
const CYAN_18 = 'rgba(0, 250, 200, 0.18)';
/** --evo-bg-0, para el texto sobre el chip cyan */
const INK = '#0B0F12';
/** --evo-fg-3, para lo que se marca pero no se puede editar */
const MUTED = '#7A8894';
const MUTED_FILL = 'rgba(122, 136, 148, 0.10)';

const CURSOR_STYLE_ID = '__evo-select-cursor';

export type OverlayKind = 'hover' | 'selection';

/**
 * Un elemento se puede marcar sin poder editarse (un contenedor, una imagen).
 * Se dibuja distinto a propósito: marcar igual que lo editable y no responder al
 * clic es exactamente el bug que se reportó dos veces.
 */
export type OverlayState = 'editable' | 'locked';

const OVERLAY_IDS: Record<OverlayKind, string> = {
  hover: '__evo-hover-overlay',
  selection: '__evo-selection-overlay',
};

/** El anillo de selección va 3px afuera, para seguir leyéndose cuando además está hovereado. */
const SELECTION_INSET = 3;

const OVERLAY_STYLES: Record<OverlayKind, Partial<CSSStyleDeclaration>> = {
  hover: {
    zIndex: '2147483646',
    border: `1.5px solid ${CYAN}`,
    background: CYAN_12,
    boxShadow: `0 0 0 3px ${CYAN_12}`,
  },
  selection: {
    zIndex: '2147483645',
    border: `2.5px solid ${CYAN}`,
    background: CYAN_18,
    boxShadow: `0 0 0 2px rgba(255, 255, 255, 0.9), 0 2px 12px ${CYAN_18}`,
  },
};

function ensureOverlay(doc: Document, kind: OverlayKind): HTMLElement {
  const existing = doc.getElementById(OVERLAY_IDS[kind]);
  if (existing) return existing;

  const overlay = doc.createElement('div');
  overlay.id = OVERLAY_IDS[kind];
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '0',
    height: '0',
    pointerEvents: 'none',
    borderRadius: '4px',
    // Sin transición ni animación: la geometría tiene que pegarse al elemento
    // de inmediato. Además blinda al overlay de estilos amplios del template,
    // del tipo `* { transition: all 200ms }`.
    transition: 'none',
    animation: 'none',
    display: 'none',
    ...OVERLAY_STYLES[kind],
  });

  const label = doc.createElement('div');
  Object.assign(label.style, {
    position: 'absolute',
    left: '-2px',
    padding: '2px 7px',
    color: INK,
    background: CYAN,
    font: '600 11px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.25)',
  });
  overlay.appendChild(label);

  // Fuera de <body>, así una selección a nivel body nunca captura el overlay.
  doc.documentElement.appendChild(overlay);
  return overlay;
}

function isOverlayNode(el: HTMLElement): boolean {
  return el.id === OVERLAY_IDS.hover || el.id === OVERLAY_IDS.selection;
}

function paint(overlay: HTMLElement, kind: OverlayKind, state: OverlayState): void {
  const color = state === 'locked' ? MUTED : CYAN;
  const fill = state === 'locked' ? MUTED_FILL : (kind === 'hover' ? CYAN_12 : CYAN_18);
  overlay.style.border = kind === 'hover'
    ? `1.5px ${state === 'locked' ? 'dashed' : 'solid'} ${color}`
    : `2.5px ${state === 'locked' ? 'dashed' : 'solid'} ${color}`;
  overlay.style.background = fill;
  overlay.style.boxShadow = kind === 'hover'
    ? `0 0 0 3px ${fill}`
    : `0 0 0 2px rgba(255, 255, 255, 0.9), 0 2px 12px ${fill}`;
  const label = overlay.firstChild as HTMLElement | null;
  if (label) {
    label.style.background = color;
    label.style.color = state === 'locked' ? '#FFFFFF' : INK;
  }
}

function showOverlay(element: HTMLElement, kind: OverlayKind, state: OverlayState): void {
  const doc = element.ownerDocument;
  if (!doc?.documentElement) return;
  if (element === doc.documentElement || isOverlayNode(element)) return;

  const overlay = ensureOverlay(doc, kind);
  paint(overlay, kind, state);
  const rect = element.getBoundingClientRect();
  const inset = kind === 'selection' ? SELECTION_INSET : 0;

  overlay.style.display = 'block';
  overlay.style.top = `${rect.top - inset}px`;
  overlay.style.left = `${rect.left - inset}px`;
  overlay.style.width = `${rect.width + inset * 2}px`;
  overlay.style.height = `${rect.height + inset * 2}px`;

  const label = overlay.firstChild as HTMLElement | null;
  if (label) {
    const tag = element.tagName.toLowerCase();
    const marca = state === 'locked' ? '🔒' : (kind === 'selection' ? '✓' : '');
    label.textContent = `${marca} ${tag}`.trim();
    // Si el elemento toca el borde superior, el chip se mete adentro.
    label.style.top = rect.top - inset > 26 ? '-24px' : '3px';
  }
}

function hideOverlay(doc: Document | null | undefined, kind: OverlayKind): void {
  const overlay = doc?.getElementById(OVERLAY_IDS[kind]);
  if (overlay) overlay.style.display = 'none';
}

function removeOverlay(doc: Document | null | undefined, kind: OverlayKind): void {
  doc?.getElementById(OVERLAY_IDS[kind])?.remove();
}

export const showHoverOverlay = (el: HTMLElement, state: OverlayState = 'editable'): void =>
  showOverlay(el, 'hover', state);
export const hideHoverOverlay = (doc: Document | null | undefined): void => hideOverlay(doc, 'hover');
export const removeHoverOverlay = (doc: Document | null | undefined): void => removeOverlay(doc, 'hover');

export const showSelectionOverlay = (el: HTMLElement, state: OverlayState = 'editable'): void =>
  showOverlay(el, 'selection', state);
export const hideSelectionOverlay = (doc: Document | null | undefined): void => hideOverlay(doc, 'selection');
export const removeSelectionOverlay = (doc: Document | null | undefined): void => removeOverlay(doc, 'selection');

/**
 * Cursor crosshair en toda la página mientras se selecciona, pisando los
 * cursores por elemento (botones, links) que si no hacen parpadear el puntero.
 */
export function applySelectModeCursor(doc: Document | null | undefined): void {
  if (!doc || doc.getElementById(CURSOR_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = CURSOR_STYLE_ID;
  // Solo el cursor: distinguir editable de bloqueado con un atributo obligaría a
  // marcar la página, y los overlays no mutan el documento (ver cabecera). La
  // diferencia la comunica el overlay: sólido con ✓ vs. punteado con 🔒.
  style.textContent = '* { cursor: crosshair !important; }';
  (doc.head ?? doc.documentElement).appendChild(style);
}

export function removeSelectModeCursor(doc: Document | null | undefined): void {
  doc?.getElementById(CURSOR_STYLE_ID)?.remove();
}

/** Saca todo rastro del overlay del documento. Para antes de capturar HTML. */
export function removeAllOverlays(doc: Document | null | undefined): void {
  removeHoverOverlay(doc);
  removeSelectionOverlay(doc);
  removeSelectModeCursor(doc);
}
